/**
 * @fileoverview ClubOS Scout Reports page — scouting report table with rating breakdown
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Mock data
import { scoutReports } from "data/mockData";

// India locale
import { formatCompact } from "utils/indiaLocale";

// TODO: Replace with API call when backend is ready
// import { scoutingService } from "api";

const RECOMMENDATION_COLOR = {
  "Sign Now": "success",
  "Monitor": "info",
  "Revisit": "warning",
  "Pass": "error",
};

const ATTR_LABELS = {
  technical: "Technical",
  physical: "Physical",
  tactical: "Tactical",
  mental: "Mental",
  potential: "Potential",
};

function ScoutReports() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  // TODO: Uncomment when API is ready
  // const [reports, setReports] = useState(scoutReports);
  // useEffect(() => { scoutingService.getReports().then(setReports).catch(console.error); }, []);

  const filtered = scoutReports.filter(
    (r) =>
      r.player.toLowerCase().includes(search.toLowerCase()) ||
      r.position.toLowerCase().includes(search.toLowerCase()) ||
      r.club.toLowerCase().includes(search.toLowerCase())
  );

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Scout Reports
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Player assessments · FC Metropolitan Scouting Department
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox p={3}>
            {/* Search + actions */}
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
              <TextField
                placeholder="Search by player, position or club..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 280 }}
              />
              <SoftBox display="flex" gap={1}>
                <SoftTypography variant="caption" color="text" alignSelf="center">
                  {filtered.length} reports
                </SoftTypography>
                <SoftButton variant="gradient" color="info" size="small">
                  New Report
                </SoftButton>
              </SoftBox>
            </SoftBox>

            {/* Report list */}
            {filtered.map((report) => (
              <SoftBox key={report.id} mb={1.5}>
                {/* Header row */}
                <SoftBox
                  display="flex"
                  alignItems="center"
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid #e9ecef",
                    cursor: "pointer",
                    backgroundColor: expanded === report.id ? "#eef6ff" : "white",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                  }}
                  onClick={() => toggleExpand(report.id)}
                >
                  {/* Overall rating circle */}
                  <SoftBox
                    width={44}
                    height={44}
                    borderRadius="50%"
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr={2}
                    sx={{
                      background:
                        report.overallRating >= 8
                          ? "linear-gradient(195deg, #66bb6a, #388e3c)"
                          : report.overallRating >= 7
                          ? "linear-gradient(195deg, #42a5f5, #1565c0)"
                          : "linear-gradient(195deg, #bdbdbd, #757575)",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    }}
                  >
                    <SoftTypography variant="button" fontWeight="bold" color="white" sx={{ fontSize: "0.85rem" }}>
                      {report.overallRating}
                    </SoftTypography>
                  </SoftBox>

                  {/* Player info */}
                  <SoftBox flex={1} minWidth={0}>
                    <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <SoftTypography variant="button" fontWeight="bold">
                        {report.player}
                      </SoftTypography>
                      <SoftBadge badgeContent={report.position} color="info" size="xs" container />
                      <SoftBadge
                        badgeContent={report.recommendation}
                        color={RECOMMENDATION_COLOR[report.recommendation]}
                        size="xs"
                        container
                      />
                    </SoftBox>
                    <SoftTypography variant="caption" color="text">
                      {report.flag} {report.club} · Age {report.age} · Scouted by {report.scout}
                    </SoftTypography>
                  </SoftBox>

                  {/* Date + toggle */}
                  <SoftBox textAlign="right" flexShrink={0} ml={2}>
                    <SoftTypography variant="caption" color="text" display="block">
                      {new Date(report.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="info">
                      {expanded === report.id ? "▲ Hide" : "▼ Details"}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>

                {/* Detail panel */}
                {expanded === report.id && (
                  <SoftBox
                    p={2.5}
                    sx={{
                      backgroundColor: "#f7fbff",
                      border: "1px solid #e9ecef",
                      borderTop: "none",
                      borderRadius: "0 0 12px 12px",
                    }}
                  >
                    <Grid container spacing={3}>
                      {/* Attribute ratings */}
                      <Grid item xs={12} sm={5}>
                        <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1.5}>
                          ATTRIBUTE BREAKDOWN
                        </SoftTypography>
                        {Object.entries(ATTR_LABELS).map(([key, label]) => {
                          const val = report.attributes?.[key] ?? 0;
                          return (
                            <SoftBox key={key} mb={1}>
                              <SoftBox display="flex" justifyContent="space-between" mb={0.3}>
                                <SoftTypography variant="caption" color="text">{label}</SoftTypography>
                                <SoftTypography variant="caption" fontWeight="bold">{val}/10</SoftTypography>
                              </SoftBox>
                              <SoftBox sx={{ height: 6, borderRadius: 3, backgroundColor: "#e9ecef", overflow: "hidden" }}>
                                <SoftBox
                                  sx={{
                                    height: "100%",
                                    width: `${val * 10}%`,
                                    backgroundColor:
                                      val >= 8 ? "#4caf50" : val >= 6 ? "#17c1e8" : "#ff9800",
                                    borderRadius: 3,
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          );
                        })}
                      </Grid>

                      {/* Narrative */}
                      <Grid item xs={12} sm={7}>
                        <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1.5}>
                          SCOUT NOTES
                        </SoftTypography>
                        <SoftTypography variant="body2" color="text" sx={{ lineHeight: 1.7, fontSize: "0.85rem" }}>
                          {report.notes}
                        </SoftTypography>

                        {report.feeEstimate && (
                          <SoftBox
                            mt={2}
                            p={1.5}
                            borderRadius="md"
                            sx={{ backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7", display: "inline-block" }}
                          >
                            <SoftTypography variant="caption" fontWeight="bold" color="success">
                              Fee Estimate: {formatCompact(report.feeEstimate)}
                            </SoftTypography>
                          </SoftBox>
                        )}

                        <SoftBox mt={2} display="flex" gap={1}>
                          <SoftButton variant="gradient" color="info" size="small">
                            Add to Pipeline
                          </SoftButton>
                          <SoftButton variant="outlined" color="secondary" size="small">
                            Print Report
                          </SoftButton>
                        </SoftBox>
                      </Grid>
                    </Grid>
                  </SoftBox>
                )}
              </SoftBox>
            ))}

            {filtered.length === 0 && (
              <SoftBox py={4} textAlign="center">
                <SoftTypography variant="body2" color="text">
                  No reports match your search.
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default ScoutReports;
